import React from "react";
import Title from "../../ui/Title";

const CareSector = () => {
  return (
    <div id="care-sector" className="scroll-mt-24">
      <Title
        title="The interdependence of climate change and the care sector"
        className="mb-6"
      />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          There is a fundamental link between the act of caring for people and
          caring for the planet. Both are essential for human survival, yet both
          are exploited in the pursuit of short-term economic and material
          gains. The devastating impacts of climate change and environmental
          degradation are outcomes of these exploits, as are the immense social
          and gender inequalities arising from the exploitation of care labor.
        </p>

        <p>
          Climate change intensifies the work involved in caring for people,
          animals, plants, and places. It reduces the availability and quality
          of public services accessible to marginalized communities, and
          exacerbates the unfair distribution of unpaid care work that fuels
          gender inequality. According to a brief titled{" "}
          <a
            href="https://www.sei.org/publications/pathways-integrate-gendered-care-work-climate/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            Pathways to integrate gendered care work into climate crisis
            preparedness and response
          </a>
          , part of the G20 policy engagement, in 2019, weather-related hazards
          displaced 24.9 million people. By 2050, projections indicate that more
          than 200 million people will require humanitarian assistance each year
          as a result of climate-related disasters. This is particularly
          significant for G20 countries, both due to climate risks within their
          borders and the indirect impacts stemming from disasters in their
          supply chain countries. An European Union{" "}
          <a
            href="https://www.europarl.europa.eu/RegData/etudes/BRIE/2021/698753/EPRS_BRI(2021)698753_EN.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            briefing document
          </a>{" "}
          states that 32.6 million people globally were displaced in 2022 by
          floods, windstorms, earthquakes, or droughts, a number which could
          swell to 1.2 billion by 2050.
        </p>

        <p>
          The impacts of climate change are particularly severe in
          resource-based economies, and make everyday life difficult for people
          living with scarce resources and low incomes, especially in rural
          areas of low and middle-income countries. Care is central to climate
          justice and gender equality. Climate-related disasters
          disproportionately impact people with care responsibilities but care
          responsibilities are overwhelmingly borne by women.
        </p>

        <p className="italic">Some of these impacts are listed below:</p>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Climate change intensifies the work involved in caring for people,
            animals, plants, and places by reducing the availability and quality
            of public services, especially in marginalized communities.
          </li>
          <li>
            Climate strategies often overlook the impact climate change has on
            care work, as they do not adequately consider whether interventions
            improve or further intensify the conditions faced by carers.
          </li>
          <li>
            Gender-sensitive climate action tends to focus on women's economic
            empowerment rather than on distribution of care work. As women's
            unpaid care work often acts as a buffer against climate impacts,
            this dilutes the need for a more equitable distribution of care
            work.
          </li>
        </ul>

        <p>
          However, initial humanitarian aid tends to overlook related gendered
          vulnerabilities. As a result, targeted interventions are needed to
          address the disproportionate risks and leverage the transformative
          potential of crises.
        </p>

        <p>
          Factors that compound this unequal distribution of unpaid care work
          place a disproportionate burden on women, further widening gender
          inequities in the care space. Heavy care workloads limit women’s
          quality of life, leisure, and economic opportunities. Because care
          work is often undervalued, it also diminishes women’s social and
          economic standing.Care demands also limit women's leisure and economic
          opportunities.
        </p>

        <p>
          Climate change and care crises are highly interdependent, and
          solutions addressing both are crucial for sustainability and equity.
        </p>

        <p className="italic">
          Several care-related factors must be considered when addressing
          climate change:
        </p>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Access to fundamental resources is essential for caring for people
            and the planet.
          </li>
          <li>
            Climate change increases care demands and exacerbates inequities.
          </li>
          <li>
            Care workers are vital for community resilience and sustainable
            practices.
          </li>
          <li>
            There is a need to assess the care sector's role in climate
            resilience and decarbonization.
          </li>
        </ul>

        <p>
          All of this became glaringly obvious during the pandemic. COVID-19
          heightened care demands and exposed existing inequalities in meeting
          them. The pandemic revealed the fragility of global care systems.
        </p>

        <p>
          The need is now more than ever — the Earth has surpassed the 1.5°C
          warming barrier, highlighting the urgency of the climate crisis.
          Unfortunately, the link between care work and climate change still
          remains largely underexplored. The global community’s efforts to adapt
          to and mitigate climate change have insufficiently recognized the
          necessity of care work for human and ecosystem survival, as well as
          community resilience.
        </p>

        <p>
          Mitigation and adaptation solutions often reinforce inequalities in
          care labor, placing an even greater climate burden on minority groups,
          particularly women, girls, and structurally disadvantaged populations
          in low- and middle-income countries (LMICs). To fully address these
          disparities, incorporating an intersectional lens can help to better
          understand how multiple factors, such as gender, ethnicity, and
          socioeconomic status interact to intensify the vulnerabilities faced
          by these groups.
        </p>
      </div>
    </div>
  );
};

export default CareSector;
